import { useEffect } from 'react';
import {
  STORAGE_KEY_PRODUCT_NAME,
  STORAGE_KEY_BRAND_NAME,
  STORAGE_KEY_AUTO_GENERATE_FIRST_FRAME,
  STORAGE_KEY_AUTO_GENERATE_STORYBOARD,
  STORAGE_KEY_AUTO_GENERATE_ENDCARD_FRAME,
  STORAGE_KEY_AUTO_GENERATE_NARRATION,
  STORAGE_KEY_AUTO_APPEND_TO_PREVIOUS_SCENE,
  STORAGE_KEY_SCENARIO_COUNT,
  STORAGE_KEY_GENERATION_MODEL,
  STORAGE_KEY_THINKING_LEVEL,
  STORAGE_KEY_SCENARIO_MODEL,
  STORAGE_KEY_TONE,
  STORAGE_KEY_INCLUDE_NARRATION,
  STORAGE_KEY_INCLUDE_TELOP,
  STORAGE_KEY_INCLUDE_DIALOGUE,
  STORAGE_KEY_EXCLUDE_PEOPLE,
  STORAGE_KEY_PLAYED_VIDEOS,
  STORAGE_KEY_PLAYED_NARRATIONS,
} from '../constants';

interface LocalStorageSyncParams {
  productName: string;
  brandName: string;
  autoGenerateFirstFrame: boolean;
  autoGenerateStoryboard: boolean;
  autoGenerateEndcardFrame: boolean;
  autoGenerateNarration: boolean;
  lastFrameMode: string;
  scenarioCount: number;
  scenarioGenerationModel: string;
  thinkingLevel: string;
  scenarioModel: string;
  tone: string;
  includeNarration: string;
  includeTelop: string;
  includeDialogue: string;
  excludePeople: boolean;
  playedVideos: Set<string>;
  playedNarrations: Set<string>;
}

/**
 * localStorage との同期を行うカスタムフック。
 * 各状態値が変更されたときに対応する localStorage キーへ保存する。
 */
export function useLocalStorageSync({
  productName,
  brandName,
  autoGenerateFirstFrame,
  autoGenerateStoryboard,
  autoGenerateEndcardFrame,
  autoGenerateNarration,
  lastFrameMode,
  scenarioCount,
  scenarioGenerationModel,
  thinkingLevel,
  scenarioModel,
  tone,
  includeNarration,
  includeTelop,
  includeDialogue,
  excludePeople,
  playedVideos,
  playedNarrations,
}: LocalStorageSyncParams): void {
  // productName: 値がある場合のみ保存
  useEffect(() => {
    if (productName) {
      localStorage.setItem(STORAGE_KEY_PRODUCT_NAME, productName);
    }
  }, [productName]);

  // brandName: 値がある場合のみ保存
  useEffect(() => {
    if (brandName) {
      localStorage.setItem(STORAGE_KEY_BRAND_NAME, brandName);
    }
  }, [brandName]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_AUTO_GENERATE_FIRST_FRAME, String(autoGenerateFirstFrame));
  }, [autoGenerateFirstFrame]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_AUTO_GENERATE_STORYBOARD, String(autoGenerateStoryboard));
  }, [autoGenerateStoryboard]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_AUTO_GENERATE_ENDCARD_FRAME, String(autoGenerateEndcardFrame));
  }, [autoGenerateEndcardFrame]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_AUTO_GENERATE_NARRATION, String(autoGenerateNarration));
  }, [autoGenerateNarration]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_AUTO_APPEND_TO_PREVIOUS_SCENE, lastFrameMode);
  }, [lastFrameMode]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SCENARIO_COUNT, String(scenarioCount));
  }, [scenarioCount]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_GENERATION_MODEL, scenarioGenerationModel);
  }, [scenarioGenerationModel]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_THINKING_LEVEL, thinkingLevel);
  }, [thinkingLevel]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SCENARIO_MODEL, scenarioModel);
  }, [scenarioModel]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_TONE, tone);
  }, [tone]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_INCLUDE_NARRATION, includeNarration);
  }, [includeNarration]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_INCLUDE_TELOP, includeTelop);
  }, [includeTelop]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_INCLUDE_DIALOGUE, includeDialogue);
  }, [includeDialogue]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_EXCLUDE_PEOPLE, String(excludePeople));
  }, [excludePeople]);

  // Set<string> は JSON 配列文字列に変換して保存
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_PLAYED_VIDEOS, JSON.stringify([...playedVideos]));
  }, [playedVideos]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_PLAYED_NARRATIONS, JSON.stringify([...playedNarrations]));
  }, [playedNarrations]);
}
